/**
 * Layer 1: LayoutTheme
 *
 * Controls HOW content is displayed - the component structure, layout paradigm,
 * and interaction patterns.
 *
 * This layer determines the structural organization of UI elements, not their visual styling.
 */

export type LayoutTheme =
  | 'modern-cards'          // Contemporary card-based layouts with shadows and borders
  | 'matrix-terminal'       // Retro terminal/CLI-inspired layout with monospace fonts
  | 'mac-os-classic'        // Classic Mac OS window chrome and spatial metaphors
  | 'mac-studio'            // macOS Sonoma-inspired with frosted glass and dynamic wallpaper
  | 'executive-dashboard'   // Data-dense dashboard with charts and metrics
  | 'terminal-explorer'     // Interactive terminal portfolio browser with command-line interface

export const LAYOUT_THEMES: LayoutTheme[] = [
  'modern-cards',
  'matrix-terminal',
  'mac-os-classic',
  'mac-studio',
  'executive-dashboard',
  'terminal-explorer',
]

export interface LayoutThemeConfig {
  id: LayoutTheme
  name: string
  description: string
  componentStructure: 'cards' | 'terminal' | 'windows' | 'dashboard'
  spacing: 'compact' | 'comfortable' | 'spacious'
  borders: boolean
  shadows: boolean
  animations: boolean
}

export const LAYOUT_THEME_CONFIGS: Record<LayoutTheme, LayoutThemeConfig> = {
  'modern-cards': {
    id: 'modern-cards',
    name: 'Modern Cards',
    description: 'Contemporary card-based layouts with clean design',
    componentStructure: 'cards',
    spacing: 'comfortable',
    borders: true,
    shadows: true,
    animations: true,
  },
  'matrix-terminal': {
    id: 'matrix-terminal',
    name: 'Matrix Terminal',
    description: 'Retro terminal/CLI-inspired interface',
    componentStructure: 'terminal',
    spacing: 'compact',
    borders: true,
    shadows: false,
    animations: true,
  },
  'mac-os-classic': {
    id: 'mac-os-classic',
    name: 'Mac OS Classic',
    description: 'Classic Mac OS window chrome and spatial metaphors',
    componentStructure: 'windows',
    spacing: 'comfortable',
    borders: true,
    shadows: true,
    animations: false,
  },
  'mac-studio': {
    id: 'mac-studio',
    name: 'Mac Studio',
    description: 'macOS Sonoma-inspired with frosted glass and dynamic wallpaper',
    componentStructure: 'windows',
    spacing: 'comfortable',
    borders: true,
    shadows: true,
    animations: true,
  },
  'executive-dashboard': {
    id: 'executive-dashboard',
    name: 'Executive Dashboard',
    description: 'Data-dense dashboard with charts and metrics',
    componentStructure: 'dashboard',
    spacing: 'compact',
    borders: false,
    shadows: false,
    animations: true,
  },
  'terminal-explorer': {
    id: 'terminal-explorer',
    name: 'Terminal Explorer',
    description: 'Interactive terminal portfolio browser with command-line interface',
    componentStructure: 'terminal',
    spacing: 'compact',
    borders: false,
    shadows: false,
    animations: true,
  },
}
